import {
  Box,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  DynamicPurchasingSystemType,
  type DpsSchemeDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { useReadinessByField } from "../hooks/useReadinessByField";
import { ThemedDatePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDatePicker";
import { SelectableCard } from "../../../../../common/input/SelectableCard";
import { endOfDay, startOfUtcDay } from "./schemeValidityDates";
import { SavedPeriodStays, useValidityPeriod } from "./useValidityPeriod";

interface Props {
  eventId: string;
  usage: DynamicPurchasingSystemType | null;
  scheme: DpsSchemeDto | null;
  isEditable: boolean;
}

// t('Only the contracting authorities named in the notice may buy through the system')
// t('Contracting authorities not named in the notice may also buy through the system')
const USAGE_OPTIONS = [
  {
    value: DynamicPurchasingSystemType.OnlyListedBuyers,
    descriptionKey: "Only the contracting authorities named in the notice may buy through the system",
  },
  {
    value: DynamicPurchasingSystemType.AlsoUnlistedBuyers,
    descriptionKey: "Contracting authorities not named in the notice may also buy through the system",
  },
] as const;

// Announced once and binding from then on, which is why the fields lock at publication.
export const DpsSchemeFields = ({ eventId, usage, scheme, isEditable }: Props) => {
  const { t } = useTranslation();
  const { updateField, fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const { readinessFor } = useReadinessByField(eventId);
  const validFromError = fieldError("publicContext.dpsScheme.validFrom");
  const validToError =
    fieldError("publicContext.dpsScheme.validTo") ??
    readinessFor("DpsValidityOrdered")?.error ??
    readinessFor("DpsFirstIntakeWithinValidity")?.error;
  const { validFrom, validTo, setValidity, keepsSavedPeriod } = useValidityPeriod(scheme, (period) =>
    updateField({ publicContext: { dpsScheme: period } }),
  );

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5" mb="8">
      <Heading as="h3" size="sm" mb="1">
        {t("The dynamic purchasing system")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mb="5">
        {t("Announced once. Suppliers can apply for the whole period, and every purchase runs among those admitted.")}
      </Text>

      <FormControl mb="6">
        <FormLabel fontSize="sm">{t("Who may buy through the system?")}</FormLabel>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
          {USAGE_OPTIONS.map((option) => (
            <SelectableCard
              key={option.value}
              isSelected={usage === option.value}
              isDisabled={!isEditable}
              onClick={() => {
                if (isEditable) updateField({ publicContext: { dynamicPurchasingSystem: option.value } });
              }}
            >
              <Text fontWeight="semibold" fontSize="sm">
                {t(option.value)}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t(option.descriptionKey)}
              </Text>
            </SelectableCard>
          ))}
        </SimpleGrid>
      </FormControl>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="6">
        <FormControl isInvalid={!!validFromError}>
          <FormLabel fontSize="sm">{t("The system runs from")}</FormLabel>
          <ThemedDatePicker
            date={validFrom}
            isClearable
            isDisabled={!isEditable}
            onChange={(date) => setValidity({ validFrom: date ? startOfUtcDay(date) : null })}
          />
          {validFromError && <FormErrorMessage>{formatFieldError(validFromError)}</FormErrorMessage>}
        </FormControl>
        <FormControl isInvalid={!!validToError}>
          <FormLabel fontSize="sm">{t("until")}</FormLabel>
          <ThemedDatePicker
            date={validTo}
            isClearable
            isDisabled={!isEditable}
            onChange={(date) => setValidity({ validTo: date ? endOfDay(date) : null })}
          />
          {validToError ? (
            <FormErrorMessage>{formatFieldError(validToError)}</FormErrorMessage>
          ) : (
            <FormHelperText>
              {t("Suppliers can apply right up to this date. Extending it later requires a change notice.")}
            </FormHelperText>
          )}
        </FormControl>
      </SimpleGrid>
      {keepsSavedPeriod && scheme && <SavedPeriodStays saved={scheme} />}
    </Box>
  );
};

